import React, {
    useContext, useLayoutEffect, useMemo, useRef, useState, MutableRefObject,
    useEffect,
} from 'react';
import {
    gql, useMutation, useQuery, useReactiveVar,
} from '@apollo/client';
import classNames from 'classnames';
import compose from 'lodash/flowRight';
import { BR1137Dialog } from 'app/components/Dialog/BR1137Dialog';
import { UIButton, UIRadioIcon } from 'app/components/ui';
import { SelectDropdown } from 'app/components/ui/SelectDropdown';
import { MobileCitySelectModal } from 'global/header/components/MobileCitySelectModal/MobileCitySelectModal';
import { DialogAnimated } from 'app/components/Dialog';

import { LocationContext } from 'app/containers/LocationProvider';
import withAvailableDeliveryDates from 'app/containers/withAvailableDeliveryDates';
import { clearAfterSwitchDialogTexts, isDateInRange } from 'app/containers/contexts/filters.context/filters.context';
import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { DIFFS, ONE_DAYS_IN_UTC_FORMAT } from 'app/containers/contexts/menuDates.consts';
import { formatPeriodMobile } from 'app/utils/date';
import { isGlobalLoadingVar, regionSubdivisionVar, selectedPeriodVar } from 'app/apollo/reaction';
import { analyticService } from 'global/services';
import { citiesLocales as cities } from 'app/const/location';
import { excludeHiddenPeriods } from 'app/const/hiddenPeriods';
import { TypeCities } from 'global/const/types/location';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { useBasketData, useClearBasket, useMenuPeriodsData } from 'app/hooks/accessors';
import { onCompletedBillingMutation } from 'app/containers/BasketResolver';
import { useBillingMutationVars } from 'app/hooks/accessors/useBillingMutationVars';
import { BASKET_PAGE_ROOT_QUERY_INITIAL_COPY } from 'app/hooks/accessors/useBasketData';
import { MENU_DISHES_BILLING_QUERY } from 'app/hooks/useBillingMutation';
import css from './mobileDeliveryDateSelectPopup.module.scss';

const addressPartRecord = {
    MSK: 'г Москва',
    SPB: 'г Санкт-Петербург',
    NIZ: 'г Нижний Новгород',
    KZN: 'г Казань',
    TUL: 'г Тула',
    YAR: 'г Ярославль',
    VLA: 'г Владимир',
    RYA: 'г Рязань',
    KLU: 'г Калуга',
    TVE: 'г Тверь',
    VOR: 'г Воронеж',
};

const CART_DATA_FOR_DATE_SELECT_POPUP_QUERY = gql`
    query CART_DATA_FOR_DATE_SELECT_POPUP(
        $period: String!
    ) {
        cart(
            period: $period
        ) {
            type
            id
            period
            sections {
                id
                cartType
            }
        }
    }
`;

// const CITY_SELECT_BILLING_MUTATION = gql`
//     mutation CITY_SELECT_BILLING_MUTATION(
//         $promocode: String,
//         $period: String!,
//         $payment_method: paymentMethod,
//         $phone: String,
//     ) {
//         billing(
//             promocode: $promocode,
//             period: $period,
//             payment_method: $payment_method,
//             phone: $phone,
//         ) {
//             promocode
//             payment_method
//             isNewbie
//         }
//     }
// `;

const CITY_SELECT_BILLING_MUTATION = gql`
    mutation CITY_SELECT_BILLING_MUTATION(
        $promocode: String,
        $period: String!,
        $payment_method: paymentMethod,
        $phone: String,
    ) {
        billing(
            promocode: $promocode,
            period: $period,
            payment_method: $payment_method,
            phone: $phone,
        ) {
            promocode
            payment_method
            isNewbie
            cart {
                period
                isSubscriptionActivated
                id
                typeOfSet
                haveDishesFromTrial
                isSectionsEmpty @client
                type
                __typename
                sections {
                    id
                    title
                    gift_category
                    cartType
                    items {
                        ...ROOT_CART_DISH_ITEM
                        __typename
                        dish {
                            ...ROOT_MENU_DISH_ITEM
                            __typename
                        }
                    }
                    __typename
                }
                totals {
                    ...ROOT_CART_TOTAL
                    __typename
                }
                discount_conditions {
                    ...ROOT_CART_DISCOUNT_CONDITIONS
                    __typename
                }
            }
        }
    }
`;


function MobileDeliveryDateSelectPopup(props: any) {
    const {
        setIsOpen, deliveryDates,
        realm = 'default',
        onSelectPeriod = null,
    } = props;
    const [clearBasketDialogOpen, setClearBasketDialogOpen] = useState(false);
    const regionSubdivision = useReactiveVar(regionSubdivisionVar);

    const billingVars = useBillingMutationVars();

    const {
        locationKey,
        mutateAddreessHandler,
    } = useContext(LocationContext);
    const { locale } = useContext(LocaleContext);

    const { periods } = useMenuPeriodsData();
    const [currentPeriods, setCurrentPeriods] = useState(periods);


    useEffect(
        () => {
            setCurrentPeriods(periods);
        },
        [periods],
    );

    const selectedPeriod = selectedPeriodVar();

    const [uiSelectedPeriod, setUiSelectedPeriod] = useState(selectedPeriod);
    const [isMobileSelectOpenned, setIsMobileSelectOpenned] = useState<boolean>(false);

    const clearBasket = useClearBasket();

    const { cart } = useBasketData(CART_DATA_FOR_DATE_SELECT_POPUP_QUERY);

    // SL
    const isBasketEmpty = useMemo(
        // @ts-ignore
        () => cart?.sections?.length === 0,
        [cart],
    );

    const {
        state: menuDatesContext,
        onSelectDate,
    } = useContext(menuDatesState);

    const [selectedRegion, setSelectedRegion] = useState(locationKey);
    const [hasScroll, setHasScroll] = useState(false);
    const [isFullyScrolled, setIsFullyScrolled] = useState<boolean>(false);
    const intervalsRef = useRef<HTMLDivElement>(null);

    const [selectedDate, setSelectedDate] = useState(menuDatesContext.dateFilterStatus);

    const selectedTimeEntityRef = useRef<any>(null);

    const options: Record<string, string>[] = useMemo(
        () => cities.map((e) => ({ ...e, title: e.value })) || {},
        [],
    );
    const [selectedOption, setSelectedOption] = useState(options.find((e) => e.id === locationKey) || {});

    const onSelectClickHandler = () => {
        setIsMobileSelectOpenned((prev) => !prev);
    };

    useLayoutEffect(() => {
        const c = intervalsRef.current;

        if (!c) return;

        const hs = c.clientHeight < c.scrollHeight;
        setHasScroll(hs);
    }, []);

    useLayoutEffect(() => {
        const c = intervalsRef.current;
        if (!c) return undefined;
        const handleScroll = () => {
            const fullyScrolled = c.scrollTop + c.clientHeight >= c.scrollHeight;
            setIsFullyScrolled(fullyScrolled);
        };
        c.addEventListener('scroll', handleScroll);
        return (): void => {
            c.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(
        () => {
            setUiSelectedPeriod(selectedPeriod);
        },
        [selectedPeriod],
    );

    // -------------------------------------------------------------------------
    const daysPeriodSection = useMemo(() => {
        const daysPeriodItems = deliveryDates
            .map(
                (dd: any) => {
                    const {
                        xValue,
                        day,
                        date,
                        month,
                    } = dd;

                    const isPeriodWithDaysSelected = uiSelectedPeriod === menuDatesContext.filterPeriod
                            || isDateInRange(uiSelectedPeriod, menuDatesContext.filterPeriod, 1);

                    const isSelectedDay = xValue === selectedDate;

                    const selected = isPeriodWithDaysSelected && isSelectedDay;
                    const dataTestId = xValue === ONE_DAYS_IN_UTC_FORMAT
                        ? 'tomorrow__mobile'
                        : 'x+day__mobile';

                    const dayText = xValue === ONE_DAYS_IN_UTC_FORMAT ? 'Завтра' : day;

                    return {
                        id: xValue,
                        selected,
                        buttonProps: {
                            onClick: () => {
                                setUiSelectedPeriod(menuDatesContext.filterPeriod);
                                setSelectedDate(xValue);
                                selectedTimeEntityRef.current = { ...selectedTimeEntityRef.current, lastEntityModified: 'day' };

                                if (!selectedTimeEntityRef.current?.dateModified) {
                                    selectedTimeEntityRef.current.dateModified = true;
                                }
                            },
                            name: xValue,
                            'data-test-id': dataTestId,
                        },
                        date,
                        dateText: `${dayText}, ${date} ${month}`,
                    };
                });
        return {
            id: 'daysPeriodSection',
            items: daysPeriodItems,
        };
    }, [
        deliveryDates,
        menuDatesContext.filterPeriod,
        selectedDate,
        uiSelectedPeriod,
        locationKey,
    ]);

    const isSpecificRealm = useMemo(
        () => realm === 'specific',
        [realm],
    );

    const weekPeriodsSection = useMemo(() => {
        const visibleFuturePeriods = currentPeriods
            .filter(({ isVisibleInFilter }: any) => isVisibleInFilter === 'visible')
            .filter((p: any) => p.start !== menuDatesContext.filterPeriod);

        const weekPeriodsItems = excludeHiddenPeriods(visibleFuturePeriods).map((period: any) => {
            // @ts-ignore
            const { start, end } = formatPeriodMobile(period, uiSelectedPeriod);

            const isPeriodWithDaysSelected = period.start === uiSelectedPeriod;
            const selected = isPeriodWithDaysSelected;

            // const isQuaziPeriod = extraPeriodNyDates[extraPeriodNYStart][0] === period.start;
            // const quaziPeriodDate = selectedRegion === 'MSK' ? `${start.day} ${start.month}` : `${end.day} ${end.month}`;
            // const dateText = isQuaziPeriod
            //     ? quaziPeriodDate
            //     : `${start.day} ${start.month} — ${end.day} ${end.month}`;

            const dateText = `${start.day} ${start.month} — ${end.day} ${end.month}`;

            return {
                id: period.end,
                start: period.start,
                title: period?.title || null,
                selected,
                buttonProps: {
                    onClick: () => {
                        setUiSelectedPeriod(period.start);
                        selectedTimeEntityRef.current = { ...selectedTimeEntityRef.current, lastEntityModified: 'period' };

                        if (!selectedTimeEntityRef.current?.dateModified) {
                            selectedTimeEntityRef.current.dateModified = true;
                        }
                    },

                    'data-test-id': 'period__mobile',
                },
                dateText,
            };
        })
            .filter((i: any) => Boolean(i))
            .filter((p: any) => p.start !== '2025-12-22' && p.start !== '2025-12-23');

        return {
            id: 'weekPeriodsSection',
            items: weekPeriodsItems,
        };
    },
    [currentPeriods, menuDatesContext.filterPeriod, uiSelectedPeriod, selectedRegion],
    );


    const handleChangeRegion = (region: TypeCities): void => {
        setSelectedRegion(region);
        const selectedOptionItem = options.find((e) => e.id === region);

        if (selectedOptionItem) {
            setSelectedOption(selectedOptionItem);
        }

        if (selectedTimeEntityRef.current && !('regionModified' in selectedTimeEntityRef.current)) {
            selectedTimeEntityRef.current.regionModified = true;
            return;
        }
        selectedTimeEntityRef.current = {
            ...selectedTimeEntityRef.current,
            regionModified: true,
        };
    };

    const sendAnalytics = () => {
        const selectedTimeEntity = selectedTimeEntityRef?.current;
        const dateModified = selectedTimeEntity?.dateModified ?? false;
        const regionModified = selectedTimeEntity?.regionModified ?? false;
        const itemKey = selectedTimeEntityRef?.current?.lastEntityModified || 'period';

        const analyticsEventsToSend = [
            {
                eventName: 'Filter_Change_City',
                filter_value: selectedRegion,
                check: regionModified,
                eventAction: 'Change',
                eventLabel: 'City',
            },
            {
                eventName: 'Filter_Change_Week',
                itemKey,
                action: 'Change_Week',
                periodIndex: currentPeriods.findIndex((p: any) => p.start === uiSelectedPeriod),
                index: DIFFS.indexOf(selectedDate),
                check: dateModified,
            },
        ];
        analyticsEventsToSend
            .filter((event) => Boolean(event.check))
            .forEach((event) => {
                analyticService.push(event);
            });
    };

    // const [mutateBilling] = useMutation(CITY_SELECT_BILLING_MUTATION, {
    //     onCompleted: (res: any) => { onCompletedBillingMutation(res); },
    // });

    const submitForm = () => {
        // @ts-ignore
        const address = addressPartRecord[selectedRegion];


        mutateAddreessHandler({ address });

        if (isSpecificRealm) {
            onSelectPeriod(menuDatesContext.filterPeriod);
        } else {
            onSelectDate(selectedDate, menuDatesContext.filterPeriod);
        }
        selectedPeriodVar(uiSelectedPeriod);
        sendAnalytics();

        setIsOpen(false);
    };


    const [updateBilling] = useMutation(CITY_SELECT_BILLING_MUTATION);


    // next onSubmitHandler
    const switchToNextPeriodOrCity = () => {
        isGlobalLoadingVar(true);
        updateBilling({
            onCompleted: (data) => { onCompletedBillingMutation(data); },
            variables: {
                ...billingVars,
                payment_method: billingVars?.payment_method,
                period: uiSelectedPeriod,
            },
            update(cache, { data }) {
                cache.writeQuery({
                    query: BASKET_PAGE_ROOT_QUERY_INITIAL_COPY,
                    variables: { period: uiSelectedPeriod },
                    data: { cart: data.billing.cart },
                });
            },
            refetchQueries: [
                {
                    query: MENU_DISHES_BILLING_QUERY,
                    context: { message: 'global:refetch:useBillingMutation' },
                    variables: { period: selectedPeriodVar() },
                },
            ],


        }).then(({ data }) => {
            const cartPeriod = data?.billing?.cart?.period;
            const isCartPeriodExist = periods.find((p: any) => p.start === cartPeriod);
            const withPossibleCartPeriod = isCartPeriodExist
                ? cartPeriod || uiSelectedPeriod
                : uiSelectedPeriod;

            selectedPeriodVar(withPossibleCartPeriod);
            submitForm();
        }).finally(() => isGlobalLoadingVar(false));
    };

    const checkClearBasket = (period: string) => {
        const targetPeriod = currentPeriods.find((p: any) => p.start === period);
        const isNeedToClearBasket = targetPeriod?.clearAfterSwitchFrom || false;

        if (isNeedToClearBasket && !isBasketEmpty) {
            setClearBasketDialogOpen(true);
            return;
        }

        switchToNextPeriodOrCity();
        // submitForm();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const itemsList = [...daysPeriodSection.items, ...weekPeriodsSection.items];

    return (
        // @ts-ignore
        <BR1137Dialog
            handleClose={() => setIsOpen(false)}
        >
            <div className={css.deliveryDateSelectRoot}>
                {/*  */}
                <div className={css.deliveryDateSelectTitle}>
                    Город доставки
                </div>
                {/*  */}
                <div
                    className={css.selectCityDropdownWrapper}
                    onClick={onSelectClickHandler}
                    role="none"
                >
                    <SelectDropdown<TypeCities>
                        options={cities}
                        callback={() => { }}
                        selectedOptionProp={selectedOption}
                    />
                    <MobileCitySelectModal
                        isOpen={isMobileSelectOpenned}
                        setIsOpen={setIsMobileSelectOpenned}
                        setCityCallback={(region: any) => handleChangeRegion(region)}
                        cities={cities}
                        showSubmitButton={false}
                    />
                </div>
                <div className={css.deliveryDateSelectTitle}>
                    Дата доставки
                </div>
                <div
                    className={css.deliveryDateSelectListScrollWrapper}
                    ref={intervalsRef}
                >
                    <div
                        className={css.deliveryDateSelectList}
                        onTouchMove={(e) => e.stopPropagation()}
                    >
                        {itemsList.map((item: any) => (
                            <button
                                className={css.deliveryDateSelectListItem}
                                onClick={() => {
                                    item.buttonProps.onClick();
                                }}
                                type="button"
                                key={item.id}
                            >
                                <div className={classNames(
                                    css.deliveryDateSelectListItemText,
                                    item.selected ? css.selected : '',
                                )}
                                >
                                    {item?.title ? item?.title[locale] : item.dateText}
                                </div>
                                <div className={css.deliveryDateSelectListItemCheckbox}>
                                    <UIRadioIcon checked={item.selected} />
                                </div>
                            </button>
                        ))}
                    </div>
                    <div
                        style={{
                            opacity: (!isFullyScrolled && hasScroll) ? 1 : 0,
                        }}
                        className={css.dateSelectListContainerShadow}
                    />
                </div>
            </div>
            <div className={css.deliveryDateSelectSubmitWrapper}>
                <UIButton onClick={() => checkClearBasket(uiSelectedPeriod)}>
                    Готово
                </UIButton>
            </div>
            <DialogAnimated
                // @ts-ignore
                {...clearAfterSwitchDialogTexts[locale]}
                isOpen={Boolean(clearBasketDialogOpen)}
                onConfirm={() => {
                    clearBasket();
                    setIsOpen(false);
                    submitForm();
                    setClearBasketDialogOpen(false);
                    switchToNextPeriodOrCity();
                }}
                onReject={() => {
                    setClearBasketDialogOpen(false);
                }}
            />
        </BR1137Dialog>
    );
}

export default compose(
    withAvailableDeliveryDates,
)(MobileDeliveryDateSelectPopup);
